import {useState} from "react"
import AppLayout from "@/components/AppLayout"
import {LeadPageDisplay} from "@/components/leadPages"
import ShowFieldsIcon from "@material-ui/icons/KeyboardArrowDown"
import HideFieldsIcon from "@material-ui/icons/KeyboardArrowUp"
import CopyLinkIcon from "@material-ui/icons/InsertLink"
import LiveViewIcon from "@material-ui/icons/Visibility"
import {useDropzone} from "react-dropzone"
import AddPhotoAlternateIcon from "@material-ui/icons/AddPhotoAlternate"
import {s3PostFile} from "@/lib/requests/fileuploads"
import {useRouter} from "next/router"
import EditTitleIcon from "@material-ui/icons/Edit"
import CommitTitleEditIcon from "@material-ui/icons/Check"
import DoNotCommitTitleEditIcon from "@material-ui/icons/Clear"
import {trpc} from "@/lib/requests/index"
import {staticResources} from "@/lib/consts"
import type {LeadpageInit} from "@/lib/server/trpcRouters/leadpages"
import type {LeadPageType} from "@/lib/server/models/leadPage"
import { nanoid } from "nanoid"

export const DEFAULT_PIC = staticResources.favicon
export const FILE_PREFIX = "gbiz-photo"

type LeadPageMeta = LeadpageInit & {
  type: LeadPageType
}

export default function GoogleMyBusinessLeadPage(): JSX.Element {
  const router = useRouter()
  const trpcClient = trpc.useContext()

  const pageId = (router.query.pageId || "none") as string
  const pageLink = `/external/leadPage/${pageId}`

  const [showFields, setShowFields] = useState(false)
  const [copyText, setCopyText] = useState("Copy link")
  const [updateAtRecently, setUpdatedRecently] = useState(false)
  const [isUpdatingTitle, setIsUpdatingTitle] = useState(false)
  const [pageTitle, setPageTitle] = useState("")
  const [temporaryTitle, setTemporaryTitle] = useState("")
  const [injectedText, setInjectedText] = useState<LeadPageMeta>({
    firstName: "John",
    lastName: "Smith",
    professionalTitle: "REALTOR®, Broker",
    realtyHouse: "eXp Realty",
    phone: "(910) 123-5678",
    completeAddress: "110 Commerce Ave Southern Pines, NC 28387",
    pictureUrl: DEFAULT_PIC,
    cityAndState: "Fayetteville, North Carolina",
    type: "googleMyBusiness"
  })
  const [updated, setUpdated] = useState(new Date())

  const pageContent = trpc.useQuery(
    ["leadpages.findOne", {pageId}], {
      onSuccess: (res) => {
        setInjectedText(res.data as LeadPageMeta)
        setPageTitle(res.data.title)
        setUpdated(new Date(res.data.updatedAt))
      }
    }
  )

  const initializedPage = (
    !pageContent.isLoading
    && pageContent.data !== undefined
  )

  const updateTitle = trpc.useMutation("leadpages.title", {
    onSuccess: (pageId, {title}) => trpcClient.setQueryData(
      ["leadpages.list", {offset: 0}],
      (oldPages) => {
        if (!oldPages?.pages) {
          // if query hasn't been made yet then
          // do not create it now
          return {pages: [], finished: false}
        }
        const p = oldPages?.pages || []
        const pages = [...p]
        const index = pages.findIndex(({_id}) => _id === pageId)
        if (index < 0) {
          return {pages: [], finished: false}
        }
        pages.splice(index, 1, {...pages[index], title})
        return {pages, finished: oldPages?.finished || true}
      }
    ),
    onError: () => console.warn("title mutation couldn't be persisted")
  })

  const updatePageContent = trpc.useMutation("leadpages.pageText", {
    onError: () => console.warn("page content couldn't be updated")
  })

  const editPageField = (
    key: keyof typeof injectedText, 
    value: string
  ) => setInjectedText({
    ...injectedText,
    [key]: value
  })

  const getUploadUrl = trpc.useMutation("fileuploads.posturl")

  const {
    getRootProps,
    getInputProps,
    isDragActive,
  } = useDropzone({
    onDrop: async (files) => {
      if (files.length < 1) {
        return
      }
      if (!window.confirm("Are you sure you want to upload this file?")) {
        return
      }
      const file = files[0]
      try {
        const uploadConfig = await getUploadUrl.mutateAsync({
          keyPrefix: FILE_PREFIX,
          filename: `${nanoid(24)}/${file.name}`,
          contentType: file.type,
        })
        // upload file to file server
        await s3PostFile({
          file, signedUrl: uploadConfig.uploadUrl
        })
        updatePageContent.mutate({
          pageId, update: {pictureUrl: uploadConfig.fullFileKey}
        })
        editPageField("pictureUrl", uploadConfig.getUrl)
      } catch {
        window.alert("an error occurred when uploading file!")
      }
    },
    accept: "image/jpeg, image/png",
    maxFiles: 1
  })

  return (
    <AppLayout requiredAuth="user">
        <section 
          className="flex flex-col items-center w-full h-screen overflow-scroll pt-3"
        >
          <div className="w-4/5 mx-auto mb-3 flex items-center flex-wrap">
            {initializedPage ? <>
            <div className="w-1/2 mx-auto text-sm">
              {isUpdatingTitle ? <>
                <input
                  name="page-title"
                  className="text-gray-800 p-2"
                  value={temporaryTitle}
                  onChange={(e) => setTemporaryTitle(e.target.value)}
                />

                <button 
                  className="ml-2 text-green-600 hover:text-gray-100"
                  onClick={() => {
                    updateTitle.mutate({
                      pageId, title: temporaryTitle
                    })
                    setPageTitle(temporaryTitle)
                    setIsUpdatingTitle(false)
                  }}
                >
                    <CommitTitleEditIcon/>
                </button>

                <button 
                  className="ml-2 text-red-600 hover:text-gray-100"
                  onClick={() => setIsUpdatingTitle(false)}
                >
                    <DoNotCommitTitleEditIcon/>
                </button>
              </> : <>
                {pageTitle}

                <button 
                  className="ml-2 text-green-600 hover:text-gray-100"
                  onClick={() => {
                    setTemporaryTitle(pageTitle)
                    setIsUpdatingTitle(true)
                  }}
                >
                    <EditTitleIcon/>
                </button>
              </>}
            </div>

            <div className="text-right w-1/2">
              <a href={pageLink} rel="noopener" target="_blank">
                <button
                  className="mr-5 text-purple-600 hover:text-gray-100"
                >
                  <LiveViewIcon className="mr-1"/>
                  {"Live Page"}
                </button>
              </a>

              <button 
                className="mr-5 text-blue-600 hover:text-gray-100"
                onClick={() => {
                  window.navigator.clipboard.writeText(window.location.origin + pageLink)
                  setCopyText("Copied!")
                  window.setTimeout(() => {
                    setCopyText("Copy link")
                  }, 2_000)
                }}
              >
                <CopyLinkIcon className="mr-1"/>
                {copyText}
              </button>
            </div>
            </> : <>
              <div className="w-full text-center">
                {pageContent.isLoading ? <>
                    <div className="animate-pulse">
                      {"Loading..."}
                    </div>
                </> : <>
                  <div className="text-red-600">
                    {"Lead Page Not Found"}
                  </div>
                </>}
              </div>
            </>}
          </div>

          {initializedPage ? <>
            <div className="w-4/5  bg-gray-200  rounded-t text-gray-900">
              <div 
                className="cursor-pointer text-xl rounded-t bg-blue-600 py-2 text-center hover:bg-blue-700 text-gray-100"
                onClick={() => setShowFields(!showFields)}
              >
                {showFields ? <>
                  <HideFieldsIcon className="mr-1"/>
                    {"Hide Fields"}
                  </> : <>
                    <ShowFieldsIcon className="mr-1"/>
                    {"Show Fields"}
                  </>}
                </div>

                <div className="ml-1 text-xs text-green-600 bg-gray-100">
                  <span className={updateAtRecently ? "animate-pulse text-blue-600" : ""}>
                    updated: {updated.toLocaleString("en-us")}
                  </span>
                </div>
                
                <form
                  onSubmit={(e) => {
                    e.preventDefault()
                    const data = new FormData(
                      e.target as unknown as HTMLFormElement
                    )
                    // picture url should not be updated here
                    // because we are using a signed url to display
                    // to mutate picture url check the ondrop
                    // handler for dropzone
                    const serialized: Record<string, string> = {}
                    data.forEach((value, key) => {
                      serialized[key as string] = value as string
                    })
                    updatePageContent.mutate({
                      pageId, update: serialized
                    })
                    setUpdated(new Date())
                    setUpdatedRecently(true)
                    window.setTimeout(() => {
                      setUpdatedRecently(false)
                    }, 4_000)
                    setShowFields(false)
                  }}
                  className={`${showFields ? "" : "hidden"} p-2`}
                >
                  <label 
                    htmlFor="firstName"
                    className="text-xs text-gray-700 ml-1"
                  >
                    First Name
                  </label><br/>
                  <input 
                      type="text"
                      value={injectedText.firstName}
                      className="mb-1 w-2/5 px-3 py-1 rounded border border-solid border-gray-500" 
                      placeholder="First Name"
                      required
                      onChange={(e) => editPageField("firstName", e.target.value)}
                      minLength={1}
                      name="firstName"
                  /><br/>

                  <label 
                    htmlFor="lastName"
                    className="text-xs text-gray-700 ml-1"
                  >
                    Last Name
                  </label><br/>
                  <input 
                      type="text"
                      value={injectedText.lastName}
                      className="mb-1 w-2/5 px-3 py-1 rounded border border-solid border-gray-500" 
                      placeholder="Last Name"
                      required
                      onChange={(e) => editPageField("lastName", e.target.value)}
                      minLength={1}
                      name="lastName"
                  /><br/>

                  <label 
                    htmlFor="professionalTitle"
                    className="text-xs text-gray-700 ml-1"
                  >
                    {"Professional Title"}
                  </label><br/>
                  <input 
                      type="text"
                      value={injectedText.professionalTitle}
                      className="mb-1 w-2/5 px-3 py-1 rounded border border-solid border-gray-500" 
                      placeholder="Title"
                      required
                      onChange={(e) => editPageField("professionalTitle", e.target.value)}
                      minLength={1}
                      name="professionalTitle"
                  /><br/>

                  <label 
                    htmlFor="realtyHouse"
                    className="text-xs text-gray-700 ml-1"
                  >
                    Realty Company
                  </label><br/>
                  <input 
                      type="text"
                      value={injectedText.realtyHouse}
                      className="mb-1 w-2/5 px-3 py-1 rounded border border-solid border-gray-500" 
                      placeholder="Realty House"
                      required
                      onChange={(e) => editPageField("realtyHouse", e.target.value)}
                      minLength={1}
                      name="realtyHouse"
                  /><br/>

                  <label 
                    htmlFor="phone"
                    className="text-xs text-gray-700 ml-1"
                  >
                    Phone
                  </label><br/>
                  <input 
                      type="text"
                      value={injectedText.phone}
                      className="mb-1 w-2/5 px-3 py-1 rounded border border-solid border-gray-500" 
                      placeholder="Phone"
                      required
                      onChange={(e) => editPageField("phone", e.target.value)}
                      minLength={1}
                      name="phone"
                  /><br/>

                  <label 
                    htmlFor="completeAddress"
                    className="text-xs text-gray-700 ml-1"
                  >
                    Complete Address
                  </label><br/>
                  <input 
                      type="text"
                      value={injectedText.completeAddress}
                      className="mb-1 w-4/5 px-3 py-1 rounded border border-solid border-gray-500" 
                      placeholder="Address"
                      required
                      onChange={(e) => editPageField("completeAddress", e.target.value)}
                      minLength={1}
                      name="completeAddress"
                  /><br/>


                  <label 
                    htmlFor="cityAndState"
                    className="text-xs text-gray-700 ml-1"
                  >
                    City and State
                  </label><br/>
                  <input 
                      type="text"
                      value={injectedText.cityAndState}
                      className="mb-1 w-2/5 px-3 py-1 rounded border border-solid border-gray-500" 
                      placeholder="City and State"
                      required
                      onChange={(e) => editPageField("cityAndState", e.target.value)}
                      minLength={1}
                      name="cityAndState"
                  /><br/>

                  <label 
                    htmlFor="Agent Photo"
                    className="text-xs text-gray-700 ml-1"
                  >
                    Agent Photo
                  </label><br/>
                  <div className="box-content flex-center-column p-4 h-48">
                    <div
                      className={isDragActive ? "dropzone-photos dropzone-active position-rel inset-box border-5px-dashed-blue" : "dropzone-photos position-rel inset-box border-5px-transparent"}
                      {...getRootProps()}
                    >
                      <input {...getInputProps()}></input>
                      <div className="flex-center-column dropzone-display h100">
                        <AddPhotoAlternateIcon></AddPhotoAlternateIcon>
                        <span className="dropzone-text">
                          drag and drop image file here to upload
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <button
                    type="submit"
                    className="mt-3 p-2 text-sm rounded bg-blue-600 text-gray-100 hover:bg-blue-700"
                  >
                    Save & Publish
                  </button>
                </form>
            </div>
            
            <div
              className="w-4/5 mx-auto z-0"
            >
              <LeadPageDisplay 
                {...injectedText} 
                allowPopups={false}
                pageId={pageId}
              />
            </div>
          </> : <></>}
        </section>
    </AppLayout>
  )
}